/**
 * Impersonation helpers — admin "log in as" recruiter/institution.
 *
 * Storage keys:
 *  - `auth_token`               — the currently-active bearer token (may be the impersonated one)
 *  - `admin_token_backup`       — the admin's original token, restored when impersonation ends
 *  - `impersonation_active`     — JSON: { type, id, name, email, redirect_to, admin_email, started_at }
 */
const KEY_TOKEN = 'auth_token';
const KEY_ADMIN_BACKUP = 'admin_token_backup';
const KEY_IMP_INFO = 'impersonation_active';

export function isImpersonating() {
  return !!localStorage.getItem(KEY_IMP_INFO);
}

export function getImpersonationInfo() {
  try {
    const raw = localStorage.getItem(KEY_IMP_INFO);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/**
 * Persist the impersonation session:
 *  1. Back up the admin's current token (only if we don't already have one — protects against
 *     double-clicks or nested impersonation replacing the backup with an impersonation token).
 *  2. Overwrite `auth_token` with the impersonation token.
 *  3. Save the impersonation metadata so the banner + guards see the state.
 * Callers should then navigate to `info.redirect_to` and force a reload so AuthContext refetches.
 */
export function startImpersonation({ token, info, adminEmail }) {
  const currentToken = localStorage.getItem(KEY_TOKEN);
  if (currentToken && !localStorage.getItem(KEY_ADMIN_BACKUP)) {
    localStorage.setItem(KEY_ADMIN_BACKUP, currentToken);
  }
  localStorage.setItem(KEY_TOKEN, token);
  const payload = {
    ...info,
    admin_email: adminEmail || '',
    started_at: new Date().toISOString(),
  };
  localStorage.setItem(KEY_IMP_INFO, JSON.stringify(payload));
}

/**
 * Restore the admin session. Returns the URL the caller should navigate to,
 * or `/admin/login` if the backup is missing (unlikely but safe).
 */
export function exitImpersonation() {
  const backup = localStorage.getItem(KEY_ADMIN_BACKUP);
  localStorage.removeItem(KEY_IMP_INFO);
  if (backup) {
    localStorage.setItem(KEY_TOKEN, backup);
    localStorage.removeItem(KEY_ADMIN_BACKUP);
    return '/admin';
  }
  // Backup missing — force a fresh admin login.
  localStorage.removeItem(KEY_TOKEN);
  return '/admin/login';
}

/**
 * Wipe every impersonation-related storage entry. Called from AuthContext.logout so
 * a real logout never leaves a stale admin backup behind.
 */
export function clearImpersonationStorage() {
  localStorage.removeItem(KEY_IMP_INFO);
  localStorage.removeItem(KEY_ADMIN_BACKUP);
}
